import { useState, useEffect, useCallback, useRef, useContext } from "react";
import { post, C, THUMB_BASE, LOGO_BASE } from "./utils";
import AppShell, { ThemeCtx, Icon, SORT_OPTIONS } from "./AppShell";

// ── helpers ───────────────────────────────────────────────────────────────────
const PAGE_SIZE = 20;
const isImage   = (p="") => /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(p);
const fileName  = (p="") => p.split("/").pop();
const stripHtml = (h="") => h.replace(/<[^>]*>/g," ").replace(/&nbsp;/g," ").replace(/\s+/g," ").trim();
const getPrimaryProduct    = (arr=[]) => arr.find(p=>(p.PRIMARY==="1"||p.PRIMARY===1)&&String(p.BRANDID)!=="0")?.BRANDNAME||"";
const getPrimaryIndication = (arr=[]) => arr.find(i=>(i.PRIMARY==="1"||i.PRIMARY===1)&&String(i.INDICATIONID)!=="0")?.INDICATIONNAME||"";
const getCompanyName = (c) => (c&&String(c.SPONSORID)!=="0")?c.SPONSORNAME||"":"";
const getCompanyLogo = (c) => (c&&String(c.SPONSORID)!=="0"&&c.COMPANY_LOGO)?LOGO_BASE+c.COMPANY_LOGO:null;

const openDocTab = (doc, subId) => {
  const base = window.location.origin + window.location.pathname;
  window.open(`${base}?doc=${doc.DOCUMENTID}&dt=${doc.DOCTYPEID}&sub=${subId||""}`, "_blank", "noopener,noreferrer");
};

// ── StatRow ───────────────────────────────────────────────────────────────────
const StatRow = ({fav,attach,likes,imgCount,comments}) => (
  <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
    {fav&&<Icon name="star" size={11} color="#EF9F27"/>}
    {[{n:"attach",v:attach},{n:"like",v:likes},{n:"image",v:imgCount},{n:"chat",v:comments}].map(s=>(
      <div key={s.n} style={{display:"flex",alignItems:"center",gap:"2px",fontSize:"12px",color:C.hint}}>
        <Icon name={s.n} size={11} color={C.hint}/><span>{s.v}</span>
      </div>
    ))}
  </div>
);

const Spinner = () => (
  <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:"8px",padding:"2rem",color:C.muted,fontSize:"14px"}}>
    <div style={{width:"16px",height:"16px",border:`2px solid ${C.border}`,borderTopColor:C.primary,borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
    Loading...
    <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
  </div>
);

// ── Tile — identical to Dashboard GalleryTile ─────────────────────────────────
function GalleryTile({ doc, onOpen }) {
  const [hovered,setHovered] = useState(false);
  const chatbot    = Array.isArray(doc.CHATBOT)     ? doc.CHATBOT     : [];
  const companiesArr = Array.isArray(doc.COMPANY) ? doc.COMPANY : [];
  const company    = companiesArr.find(c=>c.PRIMARY===1||c.PRIMARY==="1") || companiesArr[0] || null;
  const products   = Array.isArray(doc.PRODUCTS)    ? doc.PRODUCTS    : [];
  const indications= Array.isArray(doc.INDICATIONS) ? doc.INDICATIONS : [];
  const imgFiles   = chatbot.filter(a=>isImage(a.FILEPATH));
  const thumbUrl   = imgFiles.length>0 ? THUMB_BASE+fileName(imgFiles[0].FILEPATH) : null;
  const logoUrl    = getCompanyLogo(company);
  const sponsor    = getCompanyName(company);
  const product    = getPrimaryProduct(products);
  const indication = getPrimaryIndication(indications);
  const notes      = stripHtml(doc.NOTES||"");
  const date       = doc.DISPLAY_TIME||"";
  const fav        = doc.MYFAVOURITES==="YES";

  const ctx      = useContext(ThemeCtx)||{};
  const theme    = ctx.theme||{};
  const grad     = theme.tileGrad||`linear-gradient(180deg,${C.primary} 0%,#1a6fba 100%)`;
  const tPrimary = theme.primary||C.primary;

  return(
    <div onClick={onOpen?()=>onOpen(doc):undefined}
      onMouseEnter={()=>setHovered(true)} onMouseLeave={()=>setHovered(false)}
      style={{background:"#fff",borderRadius:"10px",border:`1px solid ${hovered?tPrimary:C.border}`,
        overflow:"hidden",display:"flex",flexDirection:"row",cursor:"pointer",
        transform:hovered?"translateY(-2px)":"none",transition:"all 0.15s",
        boxShadow:hovered?`0 4px 20px ${tPrimary}22`:"none"}}>
      <div style={{width:"4px",background:grad,flexShrink:0}}/>
      <div style={{flex:1,display:"flex",flexDirection:"column"}}>
        <div style={{display:"flex",alignItems:"flex-start",padding:"10px",gap:"8px"}}>
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:"4px",flexShrink:0,width:"40px"}}>
            <div style={{width:"36px",height:"36px",borderRadius:"8px",border:`1px solid ${C.border}`,
              background:C.light,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden"}}>
              {logoUrl?<img src={logoUrl} alt={sponsor} style={{width:"100%",height:"100%",objectFit:"contain"}} onError={e=>e.target.style.display="none"}/>
                :sponsor?<span style={{fontSize:"12px",fontWeight:"600",color:tPrimary}}>{sponsor[0]}</span>
                :<Icon name="image" size={14} color={C.border}/>}
            </div>
            {sponsor&&<div style={{fontSize:"10px",color:C.muted,textAlign:"center",lineHeight:"1.2",
              wordBreak:"break-word",maxWidth:"40px",overflow:"hidden",
              display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical"}}>{sponsor}</div>}
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:"13px",fontWeight:"500",color:C.text,lineHeight:"1.35",marginTop:"2px",
              display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>
              {product?<><span style={{color:tPrimary}}>{product}:</span>{" "}{doc.SUBJECT||""}</>:(doc.SUBJECT||"Untitled")}
            </div>
            {indication&&<div style={{fontSize:"12px",color:C.muted,marginTop:"1px"}}>{indication}</div>}
          </div>
          <div style={{width:"60px",height:"60px",borderRadius:"6px",border:`1px solid ${C.border}`,
            background:C.light,flexShrink:0,overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center"}}>
            {thumbUrl?<img src={thumbUrl} alt="" style={{width:"100%",height:"100%",objectFit:"contain",padding:"2px"}} onError={e=>e.target.style.display="none"}/>
              :<Icon name="image" size={16} color={C.border}/>}
          </div>
        </div>
        {notes&&<div style={{padding:"0 10px 6px",fontSize:"12px",color:C.muted,lineHeight:"1.45",
          display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{notes}</div>}
        <div style={{display:"flex",alignItems:"center",padding:"6px 10px 8px",borderTop:"1px solid #EEF4FB",marginTop:"auto"}}>
          <span style={{fontSize:"11px",color:C.hint,flex:1}}>{date}</span>
          <StatRow fav={fav} attach={doc.COUNT||0} likes={doc.LIKES||0} imgCount={doc.IMAGECOUNT||0} comments={doc.COMMENTCOUNT||0}/>
        </div>
      </div>
    </div>
  );
}

// ── Main FilteredScreen ───────────────────────────────────────────────────────
function FilteredContent({ user, subproject }) {
  const p          = new URLSearchParams(window.location.search);
  const brandid    = p.get("brandid");
  const brandname  = p.get("brandname");
  const sponsorid  = p.get("sponsorid");
  const sponsorname= p.get("sponsorname");
  const indicationid  = p.get("indicationid");
  const indicationname= p.get("indicationname");
  const congressid    = p.get("congressid");
  const congressname  = p.get("congressname");
  const stateid    = p.get("stateid")||"2";

  // clean URL immediately after reading params
  useEffect(()=>{
    window.history.replaceState({},"",window.location.pathname);
  },[]);

  // Build title from filter params
  const filterLabel = brandname||sponsorname||indicationname||congressname||"Filtered";

  const [docs,       setDocs]      = useState([]);
  const [loading,    setLoading]   = useState(false);
  const [hasMore,    setHasMore]   = useState(true);
  const [error,      setError]     = useState(null);
  const [searchInput,setSearchInput]=useState("");
  const [search,     setSearch]    = useState("");
  const [sort,       setSort]      = useState("latest");

  const pageRef     = useRef(0);
  const searchTimer = useRef(null);

  const loadDocs = useCallback(async (reset=false, overrides={}) => {
    if(!subproject) return;
    setLoading(true); setError(null);
    const first = reset ? 0 : pageRef.current*PAGE_SIZE;
    const params = {
      subprojectid: subproject.SUBPROJECTID,
      userid:       user.USERID,
      stateid:      Number(stateid)||2,
      first, last:  PAGE_SIZE,
      sort:         overrides.sort||sort,
    };
    // Apply filter
    if(brandid)      params.brandid      = brandid;
    if(sponsorid)    params.sponsorid    = sponsorid;
    if(indicationid) params.indicationid = indicationid;
    if(congressid)   params.congressid   = congressid;
    if(overrides.search!==undefined) params.search=overrides.search;
    else if(search)                  params.search=search;

    try {
      const r = await post("advance_filter", params, user.AUTH_CODE);
      if(r.STATUS==="1"&&Array.isArray(r.DATA)&&r.DATA.length>0){
        setDocs(prev=>reset?r.DATA:[...prev,...r.DATA]);
        setHasMore(r.DATA.length===PAGE_SIZE);
        pageRef.current = reset ? 1 : pageRef.current+1;
      } else {
        if(reset) setDocs([]);
        setHasMore(false);
      }
    } catch { setError("Failed to load."); }
    setLoading(false);
  },[subproject, user, search, sort, brandid, sponsorid, indicationid, congressid, stateid]);

  useEffect(()=>{
    pageRef.current=0; setDocs([]); setHasMore(true);
    loadDocs(true);
  },[subproject?.SUBPROJECTID]);

  const handleSearch = val=>{
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current=setTimeout(()=>{
      setSearch(val); pageRef.current=0; setDocs([]); setHasMore(true);
      loadDocs(true,{search:val});
    },500);
  };

  const handleSort = val=>{
    setSort(val); pageRef.current=0; setDocs([]); setHasMore(true);
    loadDocs(true,{sort:val});
  };

  const ctx    = useContext(ThemeCtx)||{};
  const tPrimary = ctx.theme?.primary||C.primary;

  return(
    <div style={{padding:"20px"}}>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",gap:"12px",marginBottom:"16px"}}>
        <div>
          <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
            <button onClick={()=>window.close()}
              title="Close this tab"
              style={{display:"flex",alignItems:"center",gap:"4px",padding:"4px 10px",
                borderRadius:"6px",border:`1px solid ${C.border}`,background:"#fff",
                cursor:"pointer",fontSize:"12px",color:C.muted,fontFamily:"inherit"}}>
              ← Close
            </button>
            <div style={{fontSize:"19px",fontWeight:"500",color:C.text}}>{filterLabel}</div>
          </div>
          <div style={{fontSize:"13px",color:C.muted,marginTop:"1px"}}>
            {subproject?.SUMMARY} · {docs.length} items
            {brandname    && <span style={{marginLeft:"6px",padding:"2px 8px",borderRadius:"4px",
              fontSize:"11px",fontWeight:"600",background:`${tPrimary}15`,color:tPrimary}}>
              Product: {brandname}</span>}
            {sponsorname  && <span style={{marginLeft:"6px",padding:"2px 8px",borderRadius:"4px",
              fontSize:"11px",fontWeight:"600",background:`${tPrimary}15`,color:tPrimary}}>
              Company: {sponsorname}</span>}
            {indicationname&&<span style={{marginLeft:"6px",padding:"2px 8px",borderRadius:"4px",
              fontSize:"11px",fontWeight:"600",background:`${tPrimary}15`,color:tPrimary}}>
              Indication: {indicationname}</span>}
            {congressname &&<span style={{marginLeft:"6px",padding:"2px 8px",borderRadius:"4px",
              fontSize:"11px",fontWeight:"600",background:`${tPrimary}15`,color:tPrimary}}>
              Congress: {congressname}</span>}
            {!congressname&&congressid&&<span style={{marginLeft:"6px",padding:"2px 8px",borderRadius:"4px",
              fontSize:"11px",fontWeight:"600",background:`${tPrimary}15`,color:tPrimary}}>
              Congress #{congressid}</span>}
          </div>
        </div>
        <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:"8px"}}>
          <div style={{display:"flex",alignItems:"center",background:"#fff",
            border:`1px solid ${C.border}`,borderRadius:"8px",padding:"0 10px",gap:"6px",height:"34px"}}>
            <Icon name="search" size={13} color={C.hint}/>
            <input value={searchInput} onChange={e=>handleSearch(e.target.value)}
              placeholder="Search..."
              style={{border:"none",outline:"none",fontSize:"14px",color:C.text,background:"none",width:"160px"}}/>
          </div>
          <div style={{position:"relative",display:"flex",alignItems:"center"}}>
            <select value={sort} onChange={e=>handleSort(e.target.value)}
              style={{padding:"6px 28px 6px 10px",borderRadius:"8px",border:`1px solid ${C.border}`,
                background:"#fff",color:C.muted,fontSize:"13px",outline:"none",cursor:"pointer",appearance:"none"}}>
              {SORT_OPTIONS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <div style={{position:"absolute",right:"8px",pointerEvents:"none"}}>
              <Icon name="chevron" size={12} color={C.hint}/>
            </div>
          </div>
        </div>
      </div>

      {/* Error */}
      {error&&<div style={{padding:"10px 14px",borderRadius:"8px",fontSize:"14px",
        marginBottom:"1rem",background:C.errorBg,color:C.error}}>{error}</div>}

      {/* Empty */}
      {docs.length===0&&!loading&&(
        <div style={{textAlign:"center",padding:"4rem 0"}}>
          <Icon name="attach" size={40} color={C.border}/>
          <div style={{fontSize:"15px",color:C.muted,marginTop:"1rem"}}>No documents found</div>
        </div>
      )}

      {/* Grid */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:"12px"}}>
        {docs.map(doc=>(
          <GalleryTile key={doc.DOCUMENTID} doc={doc}
            onOpen={d=>openDocTab(d, subproject?.SUBPROJECTID)}/>
        ))}
      </div>

      {loading&&<Spinner/>}

      {hasMore&&!loading&&docs.length>0&&(
        <div style={{textAlign:"center",marginTop:"1.5rem"}}>
          <button onClick={()=>loadDocs(false)}
            style={{padding:"9px 28px",borderRadius:"8px",border:`1px solid ${C.border}`,
              background:"#fff",color:C.primary,fontSize:"14px",fontWeight:"500",cursor:"pointer"}}>
            Load more
          </button>
        </div>
      )}
    </div>
  );
}

export default function FilteredScreen({ session, onLogout }) {
  // Render inside AppShell WITHOUT overrideContent so the left nav works normally.
  // FilteredContent is shown as the main page content.
  // User can click any nav item to navigate away, or close the tab to go back.
  return (
    <AppShell session={session} onLogout={onLogout}
      overrideContent={
        <FilteredContent
          user={session?.user}
          subproject={session?.subproject}/>
      }
      hideNav={false}
      hideFilterButtons={true}
      allowNavOverride={true}/>
  );
}